import React, { useState, useEffect, useContext, useRef } from "react";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { Trash2, ArrowUpDown, Users, ChevronRight, Pencil } from "lucide-react";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const initialLoad = useRef(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const { user, accessToken, language } = useContext(AuthContext);
  const currentUserId = user?.user_id || user?.id || null;
  const superAdminId = parseInt(process.env.REACT_APP_SUPER_ADMIN_ID, 10);

  const [sortVisible, setSortVisible] = useState(false);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [refreshFlag, setRefreshFlag] = useState(false);
  const [deletePopup, setDeletePopup] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [rolePopup, setRolePopup] = useState(false);
  const [userToEditRole, setUserToEditRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");

  const translations = {
    el: {
      title: "Διαχείριση χρηστών",
      sort: "Ταξινόμηση",
      username: "Όνομα χρήστη",
      email: "Email",
      fullName: "Όνομα",
      lastName: "Επώνυμο",
      role: "Ρόλος",
      discounts: "Προσφορές",
      avgRating: "Μέση βαθμολογία",
      actions: "Ενέργειες",
      sortBy: "Ταξινόμηση κατά",
      registeredAt: "Ημερομηνία εγγραφής",
      asc: "Αύξουσα",
      desc: "Φθίνουσα",
      az: "Α-Ω",
      za: "Ω-Α",
      noUsers: "Δεν βρέθηκαν χρήστες.",
      delTitle: "Διαγραφή Χρήστη",
      delMsg: "Είστε σίγουροι ότι θέλετε να διαγράψετε αυτόν τον χρήστη; Θα διαγραφούν και όλες οι προσφορές του.",
      cancel: "Ακύρωση",
      delete: "Διαγραφή",
      loadMore: "Φόρτωση περισσότερων...",
      noMoreUsers: "Δεν υπάρχουν άλλοι χρήστες",
      roleCustomer: "Μέλος",
      roleAdmin: "Διαχειριστής",
      editRole: "Αλλαγή ρόλου",
      editRoleTitle: "Αλλαγή ρόλου χρήστη",
      editRoleMsg: "Επιλέξτε νέο ρόλο για τον χρήστη",
      save: "Αποθήκευση",
      roleSuperAdmin: "Super admin"
    },
    en: {
      title: "Manage users",
      sort: "Sort",
      username: "Username",
      email: "Email",
      fullName: "First name",
      lastName: "Last name",
      role: "Role",
      discounts: "Offers",
      avgRating: "Avg rating",
      actions: "Actions",
      sortBy: "Sort by",
      registeredAt: "Registeration date",
      asc: "Ascending",
      desc: "Descending",
      az: "A-Z",
      za: "Z-A",
      noUsers: "No users found.",
      delTitle: "Delete User",
      delMsg: "Are you sure you want to delete this user? All their offers will also be deleted.",
      cancel: "Cancel",
      delete: "Delete",
      loadMore: "Loading more...",
      noMoreUsers: "No more users",
      roleCustomer: "Member",
      roleAdmin: "Admin",
      roleSuperAdmin: "Super admin",
      editRole: "Change role",
      editRoleTitle: "Change user role",
      editRoleMsg: "Select a new role for this user",
      save: "Save",
    }
  };

  const t = translations[language] || translations.el;

  const isAlphabeticalSortField = (field) => ["username", "email", "full_name", "last_name", "role"].includes(field);
  const getOrderLabel = (value) => {
    if (isAlphabeticalSortField(sortField)) {
      return value === "asc" ? t.az : t.za;
    }
    return value === "asc" ? t.asc : t.desc;
  };

  const getRoleLabel = (role, isSuperAdmin = false) => {
    if (isSuperAdmin) return t.roleSuperAdmin;
    if (role === "admin") return t.roleAdmin;
    return t.roleCustomer;
  };

  // Fetch users (page 1 or on sort/refresh change)
  useEffect(() => {
    const fetchUsers = async (pageToLoad = 1) => {
      if (!user || !accessToken) return;
      try {
        if (pageToLoad === 1 && initialLoad.current) ;//setLoading(true);
        else if (pageToLoad !== 1) setLoadingMore(true);

        const offset = (pageToLoad - 1) * pageSize;
        const params = new URLSearchParams({
          sortField,
          sortOrder,
          limit: pageSize,
          offset,
        });

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adminusers/allusers?${params.toString()}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await response.json();
        const fetched = data.users || [];

        if (pageToLoad === 1) setUsers(fetched);
        else setUsers(prev => [...prev, ...fetched]);

        setHasMore(fetched.length === pageSize);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        initialLoad.current = false;
      }
    };

    if (user && accessToken) {
      setUsers([]);
      setPage(1);
      setHasMore(true);
      fetchUsers(1);
    }
  }, [user, accessToken, sortField, sortOrder, refreshFlag]);

  // Load more pages
  useEffect(() => {
    const fetchMore = async () => {
      if (!user || !accessToken || page === 1) return;
      try {
        setLoadingMore(true);
        const offset = (page - 1) * pageSize;
        const params = new URLSearchParams({ sortField, sortOrder, limit: pageSize, offset });

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adminusers/allusers?${params.toString()}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await response.json();
        const fetched = data.users || [];
        setUsers(prev => [...prev, ...fetched]);
        setHasMore(fetched.length === pageSize);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingMore(false);
      }
    };
    fetchMore();
  }, [page]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setPage(prev => prev + 1);
    }, { root: null, rootMargin: '200px', threshold: 0.1 });

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore]);

  const toggleExpand = (id) => setExpandedRow(expandedRow === id ? null : id);

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/adminusers/delete/${userToDelete}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!response.ok) throw new Error("Failed to delete user");
      setRefreshFlag(prev => !prev);
      setDeletePopup(false);
      setUserToDelete(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRoleEdit = (u) => {
    setUserToEditRole(u);
    setSelectedRole(u.role);
    setRolePopup(true);
  };

  const handleRoleSave = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/adminusers/role/${userToEditRole.user_id}`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ role: selectedRole }),
        }
      );
      if (!response.ok) throw new Error("Failed to update role");
      setRefreshFlag(prev => !prev);
      setRolePopup(false);
      setUserToEditRole(null);
    } catch (err) {
      console.error(err);
    }
  };

  // if (loading) return (
  //   <div className="flex justify-center items-center h-screen w-screen bg-custom-background">
  //     {t.loadMore}
  //   </div>
  // );

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background">
        <ProfileSidebar />
        <main id="adminusers-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 xl:ml-20 text-center md:text-left">

          {/* Title + Sort button */}
          <div className="flex justify-between items-center mb-8 gap-6 w-full max-w-6xl mt-12 md:mt-0">
            <h2 className="md:text-2xl text-lg font-semibold text-custom-letters flex items-center gap-2">
              <Users className="text-custom-grad1" /> {t.title}
            </h2>
            <button
              onClick={() => setSortVisible(!sortVisible)}
              title={t.sort}
              className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
              <ArrowUpDown size={18} />
            </button>
          </div>

          {/* Sort Panel */}
          {sortVisible && (
            <div className="bg-custom-header p-4 rounded-xl shadow mb-6 w-full max-w-xs">
              <p className="text-sm font-semibold mb-2 text-custom-letters">{t.sortBy}</p>
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                className="w-full p-2 rounded border mb-3 bg-white">
                <option value="created_at">{t.registeredAt}</option>
                <option value="username">{t.username}</option>
                <option value="email">{t.email}</option>
                <option value="full_name">{t.fullName}</option>
                <option value="last_name">{t.lastName}</option>
                <option value="role">{t.role}</option>
                <option value="discount_count">{t.discounts}</option>
                <option value="avg_rating">{t.avgRating}</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full p-2 rounded border bg-white">
                <option value="asc">{getOrderLabel("asc")}</option>
                <option value="desc">{getOrderLabel("desc")}</option>
              </select>
            </div>
          )}

          <div className="w-full max-w-6xl bg-white/40 rounded-2xl shadow-lg p-4 sm:p-6 backdrop-blur-sm">

            {/* Desktop Table */}
            <div className="hidden xl:block">
              <table className="w-full text-sm text-custom-letters text-center border-collapse">
                <thead className="border-b font-medium">
                  <tr>
                    <th className="px-2 py-3">{t.username}</th>
                    <th className="px-2 py-3">{t.email}</th>
                    <th className="px-2 py-3">{t.fullName}</th>
                    <th className="px-2 py-3">{t.lastName}</th>
                    <th className="px-2 py-3">{t.role}</th>
                    <th className="px-2 py-3">{t.discounts}</th>
                    <th className="px-2 py-3">{t.avgRating}</th>
                    <th className="px-2 py-3 whitespace-nowrap">{t.registeredAt}</th>
                    <th className="px-2 py-3">{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? users.map(u => (
                    <tr key={u.user_id} className="border-b">
                      <td className="px-2 py-3 font-medium text-custom-letters">{u.username}</td>
                      <td className="px-2 py-3">{u.email}</td>
                      <td className="px-2 py-3">{u.full_name || "—"}</td>
                      <td className="px-2 py-3">{u.last_name || "—"}</td>
                      <td className="px-2 py-3">{getRoleLabel(u.role, u.is_super_admin)}</td>
                      <td className="px-2 py-3">{u.discount_count}</td>
                      <td className="px-2 py-3">{u.avg_rating > 0 ? `${u.avg_rating}/5` : "—"}</td>
                      <td className="px-2 py-3 whitespace-nowrap">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="px-2 py-3">
                        <div className="flex justify-center gap-2">
                          {(() => {
                            const isCurrentUser = currentUserId === u.user_id;
                            const isProtectedAdmin = u.is_super_admin || u.user_id === superAdminId;
                            const canEditRole = !isCurrentUser && !isProtectedAdmin;
                            const canDelete = !isCurrentUser && !isProtectedAdmin;
                            return (
                              <>
                                <button
                                  onClick={() => canEditRole && handleRoleEdit(u)}
                                  disabled={!canEditRole}
                                  title={canEditRole ? t.editRole : t.editRole}
                                  className={`bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full ${!canEditRole ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                  <Pencil size={18} />
                                </button>
                                <button
                                  onClick={() => {
                                    if (!canDelete) return;
                                    setUserToDelete(u.user_id);
                                    setDeletePopup(true);
                                  }}
                                  disabled={!canDelete}
                                  title={canDelete ? t.delete : t.delete}
                                  className={`bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full ${!canDelete ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                  <Trash2 size={18} />
                                </button>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="9" className="py-6 italic text-custom-letters">{t.noUsers}</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="block xl:hidden">
              {users.length === 0 && <p className="py-6 italic text-custom-letters text-center">{t.noUsers}</p>}
              {users.map(u => {
                const isExpanded = expandedRow === u.user_id;
                return (
                  <div key={u.user_id}
                    className="border border-gray-300 rounded-xl mb-4 bg-custom-background/60 shadow-sm overflow-hidden">
                    <div className="flex items-center px-3 py-3 gap-2">
                      <button
                        onClick={() => toggleExpand(u.user_id)}
                        className="p-1 hover:bg-gray-200 rounded-full transition-transform duration-200"
                        style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                        <ChevronRight size={20} className="text-gray-500" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className="block font-semibold text-custom-letters truncate text-sm">
                          {u.username}
                        </span>
                        <span className="text-xs text-custom-letters truncate block">{u.email}</span>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        {(() => {
                          const isCurrentUser = currentUserId === u.user_id;
                          const isProtectedAdmin = u.is_super_admin || u.user_id === superAdminId;
                          const canEditRole = !isCurrentUser && !isProtectedAdmin;
                          const canDelete = !isCurrentUser && !isProtectedAdmin;
                          return (
                            <>
                              <button
                                onClick={() => canEditRole && handleRoleEdit(u)}
                                disabled={!canEditRole}
                                title={canEditRole ? t.editRole : t.editRole}
                                className={`bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white p-2 rounded-full shadow-sm ${!canEditRole ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <Pencil size={13} />
                              </button>
                              <button
                                onClick={() => {
                                  if (!canDelete) return;
                                  setUserToDelete(u.user_id);
                                  setDeletePopup(true);
                                }}
                                disabled={!canDelete}
                                title={canDelete ? t.delete : t.delete}
                                className={`bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white p-2 rounded-full shadow-sm ${!canDelete ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <Trash2 size={13} />
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-10 pb-4 text-xs text-custom-letters text-left border-t border-gray-100 pt-3 bg-white/30">
                        <p className="mb-1"><span className="font-semibold">{t.fullName}:</span> {u.full_name || "—"}</p>
                        <p className="mb-1"><span className="font-semibold">{t.lastName}:</span> {u.last_name || "—"}</p>
                        <p className="mb-1"><span className="font-semibold">{t.role}:</span> {getRoleLabel(u.role, u.is_super_admin)}</p>
                        <p className="mb-1"><span className="font-semibold">{t.discounts}:</span> {u.discount_count}</p>
                        <p className="mb-1"><span className="font-semibold">{t.avgRating}:</span> {u.avg_rating > 0 ? `${u.avg_rating}/5` : "—"}</p>
                        <p><span className="font-semibold">{t.registeredAt}:</span> {new Date(u.created_at).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>

          <div ref={loaderRef} className="h-10 w-full max-w-6xl" />
          {loadingMore && <div className="text-center mt-2">{t.loadMore}</div>}
          {!hasMore && !loading && users.length > 0 && (
            <div className="text-center mt-2 text-custom-letters">{t.noMoreUsers}</div>
          )}

          {/* Role Edit Popup */}
          {rolePopup && userToEditRole && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div role="dialog" aria-modal="true" aria-labelledby="adminusers-role-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
                <h3 id="adminusers-role-title" className="text-lg font-semibold text-custom-letters mb-1">{t.editRoleTitle}</h3>
                <p className="text-sm text-custom-letters opacity-70 mb-4">{userToEditRole.username}</p>
                <p className="text-sm text-custom-letters mb-3">{t.editRoleMsg}</p>
                <select
                  value={selectedRole}
                  onChange={e => setSelectedRole(e.target.value)}
                  className="w-full p-2 rounded border mb-6 text-sm outline-none focus:ring-2 focus:ring-custom-grad1 bg-white">
                  <option value="customer">{t.roleCustomer}</option>
                  <option value="admin">{t.roleAdmin}</option>
                </select>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => { setRolePopup(false); setUserToEditRole(null); }}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition">
                    {t.cancel}
                  </button>
                  <button
                    onClick={handleRoleSave}
                    disabled={selectedRole === userToEditRole.role}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition disabled:opacity-40">
                    {t.save}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Popup */}
          {deletePopup && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div role="dialog" aria-modal="true" aria-labelledby="adminusers-delete-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
                <h3 id="adminusers-delete-title" className="text-lg font-semibold text-custom-letters mb-3">{t.delTitle}</h3>
                <p className="text-sm text-custom-letters mb-6">{t.delMsg}</p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => { setDeletePopup(false); setUserToDelete(null); }}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition">
                    {t.cancel}
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition">
                    {t.delete}
                  </button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminUsers;
